import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  MapPin, Phone, Mail, Globe, Calendar, Clock, Users, ChevronRight,
  Facebook, Youtube, Instagram, Twitter, Loader2, ArrowRight, User,
  Lock, LogIn, LogOut, Heart, X, CheckCircle, Gift
} from "lucide-react";
import api from "@/lib/api";
import OnlineGiving from "@/components/OnlineGiving";

// Member auth helpers
const getMemberToken = () => localStorage.getItem("member_token");
const getMemberData = () => {
  try {
    return JSON.parse(localStorage.getItem("member_data") || "null");
  } catch {
    return null;
  }
};

const memberApi = {
  get: (url) => api.get(url, { headers: { Authorization: `Bearer ${getMemberToken()}` } }),
  post: (url, data) => api.post(url, data, { headers: { Authorization: `Bearer ${getMemberToken()}` } }),
};

const PublicOrgPage = () => {
  const { unitCode, postSlug } = useParams();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showEventRegModal, setShowEventRegModal] = useState(false);
  const [showGivingModal, setShowGivingModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [memberData, setMemberData] = useState(getMemberData());
  const [regForm, setRegForm] = useState({});

  // Check for existing member session
  useEffect(() => {
    setMemberData(getMemberData());
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-org-page", unitCode],
    queryFn: () => api.get(`/public/org/${unitCode}`).then((r) => r.data),
  });

  // Fetch member profile if logged in
  const { data: memberProfile } = useQuery({
    queryKey: ["member-profile-public"],
    queryFn: () => memberApi.get("/member/me").then((r) => r.data),
    enabled: !!getMemberToken(),
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (formData) => api.post("/member/login", formData),
    onSuccess: (response) => {
      localStorage.setItem("member_token", response.data.access_token);
      localStorage.setItem("member_data", JSON.stringify(response.data.member));
      setMemberData(response.data.member);
      setShowLoginModal(false);
      toast.success(`Welcome back, ${response.data.member.name}!`);
      
      // If there's a pending event registration, prefill the form
      if (selectedEvent) {
        prefillRegistration(response.data.member);
      }
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Login failed");
    },
  });

  // Event registration mutation
  const registerMutation = useMutation({
    mutationFn: (formData) => api.post(`/events/${selectedEvent.event_id}/register`, formData),
    onSuccess: () => {
      toast.success("Registration successful!");
      setShowEventRegModal(false);
      setSelectedEvent(null);
      setRegForm({});
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Registration failed");
    },
  });

  const handleLogout = () => {
    localStorage.removeItem("member_token");
    localStorage.removeItem("member_data");
    setMemberData(null);
    toast.success("Logged out successfully");
  };

  const handleLogin = (e) => {
    e.preventDefault();
    loginMutation.mutate(loginForm);
  };

  const prefillRegistration = (member) => {
    setRegForm({
      name: member?.name || memberProfile?.name || "",
      email: member?.email || memberProfile?.email || "",
      phone: member?.phone || memberProfile?.phone || "",
      organization: data?.unit?.name || "",
      ...regForm
    });
  };

  const openEventRegistration = (event) => {
    setSelectedEvent(event);
    if (memberData || memberProfile) {
      prefillRegistration(memberData || memberProfile);
    }
    setShowEventRegModal(true);
  };

  const handleEventRegister = (e) => {
    e.preventDefault();
    registerMutation.mutate({
      ...regForm,
      source_unit_id: data?.unit?.id,
      source_unit_name: data?.unit?.name
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Page Not Found</h1>
          <p className="text-slate-500">The requested page could not be found.</p>
          <Link to="/" className="mt-4 inline-block text-emerald-600 hover:text-emerald-700">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const { unit, page, breadcrumbs, children, leaders, recent_posts, upcoming_events, media } = data;

  const socialIcons = {
    facebook: Facebook,
    youtube: Youtube,
    instagram: Instagram,
    twitter: Twitter,
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Online Giving Modal */}
      {showGivingModal && (
        <OnlineGiving
          unitCode={unitCode}
          unitName={unit?.name}
          onClose={() => setShowGivingModal(false)}
        />
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <User className="w-6 h-6 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Member Login</h2>
              <p className="text-sm text-slate-500">{unit?.name}</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loginMutation.isPending}
                className="w-full py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loginMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <LogIn className="w-4 h-4" />
                )}
                Sign In
              </button>
            </form>
            
            <div className="mt-4 text-center">
              <Link
                to="/member/login"
                className="text-sm text-emerald-600 hover:text-emerald-700"
                onClick={() => setShowLoginModal(false)}
              >
                Forgot password? →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Event Registration Modal */}
      {showEventRegModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowEventRegModal(false);
                setSelectedEvent(null);
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900">{selectedEvent.title}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                <Calendar className="w-4 h-4" />
                {selectedEvent.start_date}
                {selectedEvent.start_time && ` at ${selectedEvent.start_time}`}
              </p>
              {selectedEvent.location && (
                <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                  <MapPin className="w-4 h-4" />
                  {selectedEvent.location}
                </p>
              )}
            </div>

            {memberData || memberProfile ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">
                    Registering as {memberData?.name || memberProfile?.name}
                  </p>
                  <p className="text-xs text-emerald-600">Your details have been prefilled</p>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <button
                    onClick={() => {
                      setShowEventRegModal(false);
                      setShowLoginModal(true);
                    }}
                    className="font-medium underline"
                  >
                    Login as a member
                  </button>
                  {" "}to prefill your details automatically.
                </p>
              </div>
            )}
            
            <form onSubmit={handleEventRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={regForm.name || ""}
                    onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={regForm.email || ""}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={regForm.phone || ""}
                    onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Church/Organization</label>
                  <input
                    type="text"
                    value={regForm.organization || unit?.name || ""}
                    onChange={(e) => setRegForm({ ...regForm, organization: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 bg-slate-50"
                  />
                </div>
                {selectedEvent.description && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-600">{selectedEvent.description}</p>
                  </div>
                )}
              </div>
              
              <button
                type="submit"
                disabled={registerMutation.isPending}
                className="w-full py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {registerMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Complete Registration
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Top Navigation Bar */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {breadcrumbs && breadcrumbs.length > 1 && (
              <div className="hidden md:flex items-center gap-2 text-sm text-white/70">
                {breadcrumbs.slice(0, -1).map((b, i) => (
                  <React.Fragment key={b.code}>
                    {i > 0 && <ChevronRight className="w-3 h-3" />}
                    <Link to={`/org/${b.code}`} className="hover:text-white">
                      {b.name}
                    </Link>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
          
          {/* Member Login/Profile */}
          <div className="flex items-center gap-3">
            {/* Give Button */}
            <button
              onClick={() => setShowGivingModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 rounded-lg text-sm transition-colors font-medium"
            >
              <Gift className="w-4 h-4" />
              <span className="hidden sm:inline">Give</span>
            </button>

            {memberData ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/member"
                  className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{memberData.name?.split(" ")[0]}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm transition-colors"
              >
                <LogIn className="w-4 h-4" />
                <span>Member Login</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div
        className="relative bg-emerald-800 text-white"
        style={{
          backgroundImage: page?.hero_image_url ? `url(${page.hero_image_url})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-800/80" />
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {page?.hero_title || unit?.name}
          </h1>
          {page?.hero_subtitle && (
            <p className="text-xl text-white/80 max-w-2xl">{page.hero_subtitle}</p>
          )}

          {/* Service Times */}
          {page?.service_times && page.service_times.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-4">
              {page.service_times.map((s, i) => (
                <div key={s.id || `${s.day}-${s.time}-${i}`} className="bg-white/10 backdrop-blur px-4 py-3 rounded-lg">
                  <p className="font-medium">{s.name || "Service"}</p>
                  <p className="text-sm text-white/80">
                    {s.day} at {s.time}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Quick Actions for Members */}
          {memberData && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/member"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg font-medium hover:bg-emerald-50 transition-colors"
              >
                <Heart className="w-4 h-4" />
                View My Contributions
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            {page?.about_content && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">About Us</h2>
                <div
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: page.about_content }}
                />
              </section>
            )}

            {/* Mission & Vision */}
            {(page?.mission_statement || page?.vision_statement) && (
              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {page?.mission_statement && (
                  <div className="bg-emerald-50 rounded-xl p-6">
                    <h3 className="font-semibold text-emerald-800 mb-2">Our Mission</h3>
                    <p className="text-slate-700">{page.mission_statement}</p>
                  </div>
                )}
                {page?.vision_statement && (
                  <div className="bg-blue-50 rounded-xl p-6">
                    <h3 className="font-semibold text-blue-800 mb-2">Our Vision</h3>
                    <p className="text-slate-700">{page.vision_statement}</p>
                  </div>
                )}
              </section>
            )}

            {/* Core Values */}
            {page?.core_values && page.core_values.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Values</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {page.core_values.map((v, i) => (
                    <div key={`value-${v.slice(0, 20)}-${i}`} className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="font-medium text-slate-900">{v}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Leadership Team */}
            {leaders && leaders.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Leadership</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {leaders.map((l) => (
                    <div key={l.id} className="bg-white border rounded-xl p-4 text-center">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Users className="w-8 h-8 text-emerald-600" />
                      </div>
                      <p className="font-medium text-slate-900">{l.user_name}</p>
                      <p className="text-sm text-slate-500">{l.role_title}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Recent Blog Posts */}
            {recent_posts && recent_posts.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Latest Updates</h2>
                  <Link
                    to={`/org/${unitCode}/blog`}
                    className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                  >
                    View all →
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recent_posts.map((p) => (
                    <Link
                      key={p.id}
                      to={`/org/${unitCode}/blog/${p.slug}`}
                      className="bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                    >
                      {p.featured_image_url && (
                        <img
                          src={p.featured_image_url}
                          alt={p.title}
                          className="w-full h-40 object-cover"
                        />
                      )}
                      <div className="p-4">
                        <p className="text-xs text-emerald-600 uppercase mb-1">{p.category}</p>
                        <h3 className="font-semibold text-slate-900 mb-2">{p.title}</h3>
                        <p className="text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Media Gallery */}
            {media && media.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Gallery</h2>
                  <Link
                    to={`/org/${unitCode}/media`}
                    className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                  >
                    View all →
                  </Link>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {media.slice(0, 8).map((m) => (
                    <div key={m.id} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={m.thumbnail_url || m.url}
                        alt={m.title}
                        className="w-full h-full object-cover hover:scale-110 transition-transform"
                      />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Member Quick Access Card */}
            {memberData ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{memberData.name}</p>
                    <p className="text-sm text-slate-500">Member</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Link
                    to="/member"
                    className="block w-full py-2 px-3 bg-emerald-600 text-white rounded-lg text-center text-sm hover:bg-emerald-700 transition-colors"
                  >
                    Go to Member Portal
                  </Link>
                  <Link
                    to="/member?tab=contributions"
                    className="block w-full py-2 px-3 border border-emerald-600 text-emerald-600 rounded-lg text-center text-sm hover:bg-emerald-50 transition-colors"
                  >
                    View Contributions
                  </Link>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 border rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 mb-2">Are You a Member?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Login to access your contributions, messages, and register for events with one click.
                </p>
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Member Login
                </button>
              </div>
            )}

            {/* Contact Info */}
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Contact Us</h3>
              <div className="space-y-3">
                {(page?.contact_address || unit?.address) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <p className="text-slate-600">{page?.contact_address || unit?.address}</p>
                  </div>
                )}
                {(page?.contact_phone || unit?.phone) && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <a
                      href={`tel:${page?.contact_phone || unit?.phone}`}
                      className="text-slate-600 hover:text-emerald-600"
                    >
                      {page?.contact_phone || unit?.phone}
                    </a>
                  </div>
                )}
                {(page?.contact_email || unit?.email) && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <a
                      href={`mailto:${page?.contact_email || unit?.email}`}
                      className="text-slate-600 hover:text-emerald-600"
                    >
                      {page?.contact_email || unit?.email}
                    </a>
                  </div>
                )}
                {unit?.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-slate-400" />
                    <a
                      href={unit.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-emerald-600"
                    >
                      Website
                    </a>
                  </div>
                )}
              </div>

              {/* Social Links */}
              {page?.social_links && Object.keys(page.social_links).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex gap-3">
                    {Object.entries(page.social_links).map(([platform, url]) => {
                      const Icon = socialIcons[platform] || Globe;
                      return (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                        >
                          <Icon className="w-5 h-5" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Give Online Card */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Gift className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold">Give Online</h3>
                  <p className="text-sm text-white/80">Support our mission</p>
                </div>
              </div>
              <p className="text-sm text-white/90 mb-4">
                Your generosity helps us serve our community and spread hope.
              </p>
              <button
                onClick={() => setShowGivingModal(true)}
                className="w-full py-2 bg-white text-amber-600 rounded-lg font-medium hover:bg-amber-50 transition-colors"
              >
                Give Now
              </button>
            </div>

            {/* Upcoming Events with Registration */}
            {upcoming_events && upcoming_events.length > 0 && (
              <div className="bg-white border rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Upcoming Events</h3>
                <div className="space-y-4">
                  {upcoming_events.map((e) => (
                    <div key={e.id} className="border-b pb-4 last:border-0 last:pb-0">
                      <div className="flex gap-3">
                        <div className="w-12 h-12 bg-emerald-100 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                          <Calendar className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{e.title}</p>
                          <p className="text-sm text-slate-500">
                            {e.start_date}
                            {e.start_time && ` at ${e.start_time}`}
                          </p>
                        </div>
                      </div>
                      {e.registration_url ? (
                        <a
                          href={e.registration_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 block w-full py-2 text-center text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          Register Now
                        </a>
                      ) : e.event_id ? (
                        <button
                          onClick={() => openEventRegistration(e)}
                          className="mt-2 w-full py-2 text-center text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          {memberData ? "Quick Register" : "Register Now"}
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
                <Link
                  to={`/org/${unitCode}/events`}
                  className="mt-4 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                >
                  View all events <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            {/* Child Units */}
            {children && children.length > 0 && (
              <div className="bg-white border rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 mb-4">
                  {unit?.level === "assembly" ? "Home Groups" : 
                   unit?.level === "campus" ? "Assemblies" :
                   unit?.level === "district" ? "Campuses" : "Our Locations"}
                </h3>
                <div className="space-y-2">
                  {children.map((c) => (
                    <Link
                      key={c.id}
                      to={`/org/${c.code}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-emerald-50 transition-colors"
                    >
                      <span className="text-slate-700">{c.name}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="font-semibold mb-4">{unit?.name}</h4>
              <p className="text-slate-400 text-sm">
                {page?.mission_statement?.substring(0, 150)}...
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to={`/org/${unitCode}`} className="block text-slate-400 hover:text-white text-sm">
                  Home
                </Link>
                <Link to={`/org/${unitCode}/events`} className="block text-slate-400 hover:text-white text-sm">
                  Events
                </Link>
                <Link to={`/org/${unitCode}/blog`} className="block text-slate-400 hover:text-white text-sm">
                  Blog
                </Link>
                <Link to="/member/login" className="block text-slate-400 hover:text-white text-sm">
                  Member Portal
                </Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="text-slate-400 text-sm space-y-2">
                {(page?.contact_address || unit?.address) && (
                  <p>{page?.contact_address || unit?.address}</p>
                )}
                {(page?.contact_email || unit?.email) && (
                  <p>{page?.contact_email || unit?.email}</p>
                )}
                {(page?.contact_phone || unit?.phone) && (
                  <p>{page?.contact_phone || unit?.phone}</p>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} {unit?.name}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/login" className="text-slate-400 hover:text-white text-sm">
                Admin Login
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicOrgPage;
